/**
 * Chakra UI Component Style Overrides
 *
 * This file defines custom styles and variants for Chakra UI components.
 * These overrides are applied globally through the theme and ensure
 * consistent styling across the application.
 *
 * @fileoverview Component style configurations for Chakra UI v2
 */

import { ComponentStyleConfig, createMultiStyleConfigHelpers } from '@chakra-ui/react';
import { cardAnatomy } from '@chakra-ui/anatomy';

// Create multi-style config helper for Card component
const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(cardAnatomy.keys);

/**
 * Button Component Styles
 *
 * Modern dark theme button styles with gradient accents.
 * Features cyan/purple gradient for primary actions.
 */
export const Button: ComponentStyleConfig = {
  // Base styles applied to all button variants
  baseStyle: {
    fontWeight: '600',
    borderRadius: 'md',
    // Smooth transition for hover and active states
    transition: 'all 0.2s ease-in-out',
    // Focus ring for accessibility - cyan accent
    _focus: {
      boxShadow: '0 0 0 3px rgba(0, 217, 255, 0.4)',
    },
  },
  // Size variants for different button sizes
  sizes: {
    sm: {
      fontSize: 'sm',
      px: 4,
      py: 2,
    },
    md: {
      fontSize: 'md',
      px: 6,
      py: 3,
    },
    lg: {
      fontSize: 'lg',
      px: 8,
      py: 4,
    },
  },
  // Visual variants for different button styles
  variants: {
    /**
     * Solid variant - Primary action button with gradient
     * Purple to cyan gradient for modern look
     */
    solid: {
      bgGradient: 'linear(135deg, #9945FF 0%, #00D9FF 100%)',
      color: 'white',
      _hover: {
        bgGradient: 'linear(135deg, #9945FF 20%, #00D9FF 100%)',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 15px rgba(0, 217, 255, 0.3)',
        _disabled: {
          bgGradient: 'linear(135deg, #9945FF 0%, #00D9FF 100%)',
          transform: 'none',
        },
      },
      _active: {
        bgGradient: 'linear(135deg, #7A35CC 0%, #00B8D9 100%)',
        transform: 'translateY(0)',
      },
    },
    /**
     * Outline variant - Secondary action button
     * Cyan border with transparent background
     */
    outline: {
      bg: 'transparent',
      border: '2px solid',
      borderColor: 'xacto.electricCyan',
      color: 'xacto.electricCyan',
      _hover: {
        bg: 'rgba(0, 217, 255, 0.1)',
        borderColor: 'xacto.vibrantTeal',
        color: 'xacto.vibrantTeal',
        _disabled: {
          bg: 'transparent',
          color: 'xacto.electricCyan',
        },
      },
      _active: {
        bg: 'rgba(0, 217, 255, 0.2)',
      },
    },
    /**
     * Ghost variant - Tertiary/subtle action button
     * No background, subtle hover effect
     */
    ghost: {
      bg: 'transparent',
      color: 'text.secondary',
      _hover: {
        bg: 'bg.muted',
        color: 'text.primary',
      },
      _active: {
        bg: 'bg.surface',
      },
    },
    /**
     * Accent variant - Success/highlight button
     * Uses vibrant teal for positive actions
     */
    accent: {
      bg: 'xacto.vibrantTeal',
      color: 'xacto.deepNavy',
      _hover: {
        bg: '#33FFB5',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 15px rgba(0, 255, 163, 0.3)',
        _disabled: {
          bg: 'xacto.vibrantTeal',
          transform: 'none',
        },
      },
      _active: {
        bg: '#00CC82',
        transform: 'translateY(0)',
      },
    },
  },
  // Default variant and size
  defaultProps: {
    variant: 'solid',
    size: 'md',
  },
};

/**
 * Input Component Styles
 *
 * Modern dark theme input styles with cyan accent focus.
 */
export const Input: ComponentStyleConfig = {
  // Base styles for the input container
  baseStyle: {
    field: {
      borderRadius: 'md',
      transition: 'all 0.2s ease-in-out',
    },
  },
  // Size variants
  sizes: {
    sm: {
      field: {
        fontSize: 'sm',
        px: 3,
        py: 2,
        borderRadius: 'md',
      },
    },
    md: {
      field: {
        fontSize: 'md',
        px: 4,
        py: 3,
        borderRadius: 'md',
      },
    },
    lg: {
      field: {
        fontSize: 'lg',
        px: 4,
        py: 4,
        borderRadius: 'md',
      },
    },
  },
  // Visual variants - Dark theme optimized
  variants: {
    /**
     * Outline variant - Standard bordered input
     * Uses semantic tokens for color mode support
     */
    outline: {
      field: {
        border: '1px solid',
        borderColor: 'border.default',
        bg: 'bg.muted',
        color: 'text.primary',
        _placeholder: {
          color: 'text.muted',
        },
        _hover: {
          borderColor: 'text.secondary',
        },
        _focus: {
          borderColor: 'accent.primary',
          boxShadow: '0 0 0 1px var(--chakra-colors-accent-primary)',
          bg: 'bg.surface',
        },
        _invalid: {
          borderColor: 'error.500',
          boxShadow: '0 0 0 1px #FF4D4D',
        },
      },
    },
    /**
     * Filled variant - Subtle background input
     */
    filled: {
      field: {
        border: '1px solid',
        borderColor: 'transparent',
        bg: 'bg.primary',
        color: 'text.primary',
        _placeholder: {
          color: 'text.muted',
        },
        _hover: {
          bg: 'bg.muted',
        },
        _focus: {
          bg: 'bg.muted',
          borderColor: 'accent.primary',
          boxShadow: '0 0 0 1px var(--chakra-colors-accent-primary)',
        },
      },
    },
  },
  // Default variant and size
  defaultProps: {
    variant: 'outline',
    size: 'md',
  },
};

/**
 * Card Component Styles
 *
 * Modern dark theme card styles with subtle glow effects.
 */
// Card base styles using multi-part component helper
const cardBaseStyle = definePartsStyle({
  container: {
    borderRadius: 'xl',
    overflow: 'hidden',
    transition: 'all 0.2s ease-in-out',
  },
  header: {
    paddingX: 6,
    paddingY: 4,
  },
  body: {
    paddingX: 6,
    paddingY: 4,
  },
  footer: {
    paddingX: 6,
    paddingY: 4,
  },
});

// Card variants - uses semantic tokens for color mode
const cardVariants = {
  elevated: definePartsStyle({
    container: {
      bg: 'bg.surface',
      boxShadow: 'md',
      border: '1px solid',
      borderColor: 'border.default',
      _hover: {
        boxShadow: 'lg',
        transform: 'translateY(-2px)',
        borderColor: 'accent.primary',
      },
    },
  }),
  outline: definePartsStyle({
    container: {
      bg: 'bg.surface',
      border: '1px solid',
      borderColor: 'border.default',
      _hover: {
        borderColor: 'accent.primary',
      },
    },
  }),
  filled: definePartsStyle({
    container: {
      bg: 'bg.muted',
    },
  }),
  // Glow variant for special cards (dark mode only effect)
  glow: definePartsStyle({
    container: {
      bg: 'bg.surface',
      border: '1px solid',
      borderColor: 'border.default',
      boxShadow: 'cyan-glow',
      _hover: {
        borderColor: 'accent.primary',
        boxShadow: 'cyan-glow-lg',
      },
    },
  }),
};

export const Card = defineMultiStyleConfig({
  baseStyle: cardBaseStyle,
  variants: cardVariants,
  defaultProps: {
    variant: 'elevated',
  },
});

/**
 * Heading Component Styles
 *
 * Uses semantic tokens for color mode support.
 */
export const Heading: ComponentStyleConfig = {
  baseStyle: {
    fontFamily: 'heading',
    fontWeight: '700',
    color: 'text.primary',
  },
  sizes: {
    '4xl': {
      fontSize: ['4xl', '5xl', '6xl'],
      lineHeight: 1.1,
    },
    '3xl': {
      fontSize: ['3xl', '4xl', '5xl'],
      lineHeight: 1.2,
    },
    '2xl': {
      fontSize: ['2xl', '3xl'],
      lineHeight: 1.3,
    },
    xl: {
      fontSize: ['xl', '2xl'],
      lineHeight: 1.4,
    },
    lg: {
      fontSize: 'lg',
      lineHeight: 1.5,
    },
    md: {
      fontSize: 'md',
      lineHeight: 1.5,
    },
  },
  defaultProps: {
    size: 'xl',
  },
};

/**
 * Text Component Styles
 *
 * Uses semantic tokens for color mode support.
 */
export const Text: ComponentStyleConfig = {
  baseStyle: {
    fontFamily: 'body',
    color: 'text.secondary',
  },
  variants: {
    /**
     * Muted variant - Subtle/secondary text
     */
    muted: {
      color: 'text.muted',
    },
    /**
     * Accent variant - Highlighted text
     */
    accent: {
      color: 'accent.primary',
      fontWeight: '500',
    },
    /**
     * Success variant - Success highlighted text
     */
    success: {
      color: 'accent.success',
      fontWeight: '500',
    },
  },
};

/**
 * Export all component style configurations
 *
 * These are imported in theme/index.ts and applied
 * to the Chakra theme configuration.
 */
export const components = {
  Button,
  Input,
  Card,
  Heading,
  Text,
};

export default components;
